import { ReactNode } from "react";
import { DateTimePicker } from "./DateTimePicker";
import { BRAND, hexToRgba } from "@/utils/logoColors";

export type QuickDateRangeKey =
  | "today"
  | "yesterday"
  | "this_month"
  | "last_month"
  | "this_fy"
  | "last_fy";

function pad2(n: number): string {
  return String(n).padStart(2, "0");
}

function getIstNowParts() {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(new Date());
  const get = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((p) => p.type === type)?.value ?? NaN);
  let hh = get("hour");
  if (hh === 24) hh = 0;
  return { y: get("year"), m: get("month"), d: get("day"), hh, mm: get("minute") };
}

function toDatetimeLocalValue(y: number, m: number, d: number, hh: number, mm: number): string {
  return `${y}-${pad2(m)}-${pad2(d)}T${pad2(hh)}:${pad2(mm)}`;
}

function daysInMonth(y: number, m: number): number {
  return new Date(Date.UTC(y, m, 0)).getUTCDate();
}

export function getQuickDateRange(key: QuickDateRangeKey): { from: string; to: string } {
  const now = getIstNowParts();
  const nowLocal = toDatetimeLocalValue(now.y, now.m, now.d, now.hh, now.mm);

  switch (key) {
    case "today":
      return {
        from: toDatetimeLocalValue(now.y, now.m, now.d, 0, 0),
        to: nowLocal,
      };
    case "yesterday": {
      const utc = Date.UTC(now.y, now.m - 1, now.d) - 24 * 60 * 60 * 1000;
      const yd = new Date(utc);
      const y = yd.getUTCFullYear();
      const m = yd.getUTCMonth() + 1;
      const d = yd.getUTCDate();
      return {
        from: toDatetimeLocalValue(y, m, d, 0, 0),
        to: toDatetimeLocalValue(y, m, d, 23, 59),
      };
    }
    case "this_month":
      return {
        from: toDatetimeLocalValue(now.y, now.m, 1, 0, 0),
        to: nowLocal,
      };
    case "last_month": {
      let y = now.y;
      let m = now.m - 1;
      if (m < 1) { m = 12; y -= 1; }
      const lastDay = daysInMonth(y, m);
      return {
        from: toDatetimeLocalValue(y, m, 1, 0, 0),
        to: toDatetimeLocalValue(y, m, lastDay, 23, 59),
      };
    }
    case "this_fy": {
      const fyStartYear = now.m >= 4 ? now.y : now.y - 1;
      return {
        from: toDatetimeLocalValue(fyStartYear, 4, 1, 0, 0),
        to: nowLocal,
      };
    }
    case "last_fy": {
      const thisFyStartYear = now.m >= 4 ? now.y : now.y - 1;
      const lastFyStartYear = thisFyStartYear - 1;
      return {
        from: toDatetimeLocalValue(lastFyStartYear, 4, 1, 0, 0),
        to: toDatetimeLocalValue(thisFyStartYear, 3, 31, 23, 59),
      };
    }
  }
}

export const QUICK_DATE_RANGES: { key: QuickDateRangeKey; label: string; color: string }[] = [
  { key: "today",      label: "Today",                color: BRAND.orange },
  { key: "yesterday",  label: "Yesterday",            color: BRAND.purple },
  { key: "this_month", label: "This Month",           color: BRAND.green  },
  { key: "last_month", label: "Last Month",           color: BRAND.blue   },
  { key: "this_fy",    label: "This Financial Year",  color: BRAND.red    },
  { key: "last_fy",    label: "Last Financial Year",  color: BRAND.yellow },
];

export function GlobalSearchInput({
  value,
  onChange,
  onClear,
  isPending,
  placeholder = "Search by receipt, student, email, phone…",
}: {
  value: string;
  onChange: (v: string) => void;
  onClear: () => void;
  isPending: boolean;
  placeholder?: string;
}) {
  return (
    <div className="relative flex-1 min-w-52">
      <svg
        className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z"
        />
      </svg>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full text-sm pl-9 pr-8 py-1.5 rounded-md border border-gray-200 bg-white text-gray-800 outline-none focus:border-blue-300"
      />
      {isPending && (
        <span className="absolute right-2.5 top-1/2 -translate-y-1/2 w-1.5 h-1.5 rounded-full bg-blue-400 animate-pulse" />
      )}
      {!isPending && value && (
        <button
          onClick={onClear}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 bg-transparent border-none cursor-pointer text-sm leading-none"
        >
          ✕
        </button>
      )}
    </div>
  );
}

export type OrdersFilterBarProps = {
  searchInput: string;
  onSearchChange: (v: string) => void;
  onSearchClear: () => void;
  isSearchPending: boolean;
  searchPlaceholder?: string;
  dateFrom: string;
  dateTo: string;
  onDateFromChange: (v: string) => void;
  onDateToChange: (v: string) => void;
  onClearDates: () => void;
  quickRange: QuickDateRangeKey | null;
  onQuickRange: (key: QuickDateRangeKey) => void;
  /** Payment-status select (Orders). Hide on Fulfilment — stages use cards. */
  showStatus?: boolean;
  filterStatus?: string;
  onStatusChange?: (v: string) => void;
  showMethod?: boolean;
  filterMethod?: string;
  onMethodChange?: (v: string) => void;
  /** Page-size select. Hide when pagination already exposes it (e.g. Fulfilment). */
  showPageSize?: boolean;
  take: number;
  onTakeChange: (v: number) => void;
  idPrefix?: string;
  accentColor?: string;
  extraFilters?: ReactNode;
};

export function OrdersFilterBar({
  searchInput,
  onSearchChange,
  onSearchClear,
  isSearchPending,
  searchPlaceholder,
  dateFrom,
  dateTo,
  onDateFromChange,
  onDateToChange,
  onClearDates,
  quickRange,
  onQuickRange,
  showStatus = true,
  filterStatus = "",
  onStatusChange,
  showMethod = false,
  filterMethod = "",
  onMethodChange,
  showPageSize = true,
  take,
  onTakeChange,
  idPrefix = "order-filter",
  accentColor = BRAND.blue,
  extraFilters,
}: OrdersFilterBarProps) {
  return (
    <>
      <div
        className="flex flex-wrap items-center gap-2 mb-4 bg-gray-50 rounded-xl px-3 py-2.5 border"
        style={{ borderColor: hexToRgba(accentColor, 0.18) }}
      >
        <GlobalSearchInput
          value={searchInput}
          onChange={onSearchChange}
          onClear={onSearchClear}
          isPending={isSearchPending}
          placeholder={searchPlaceholder}
        />
        <div className="flex items-center gap-1.5">
          <DateTimePicker
            id={`${idPrefix}-from`}
            value={dateFrom}
            onChange={onDateFromChange}
            title="Start date & time (IST)"
            placeholder="From date & time"
            defaultTime="00:00"
            buttonStyle={{ borderColor: hexToRgba(BRAND.purple, 0.35) }}
          />
          <span className="text-xs font-semibold" style={{ color: BRAND.orange }}>→</span>
          <DateTimePicker
            id={`${idPrefix}-to`}
            value={dateTo}
            onChange={onDateToChange}
            title="End date & time (IST)"
            placeholder="To date & time"
            defaultTime="23:59"
            buttonStyle={{ borderColor: hexToRgba(BRAND.green, 0.35) }}
          />
          {(dateFrom || dateTo) && (
            <button
              onClick={onClearDates}
              className="text-xs bg-transparent border-none cursor-pointer"
              style={{ color: BRAND.red }}
              title="Clear dates"
            >
              ✕
            </button>
          )}
        </div>
        {showStatus && (
          <select
            value={filterStatus}
            onChange={(e) => onStatusChange?.(e.target.value)}
            className="text-xs px-2.5 py-1.5 rounded-md border border-gray-200 bg-white text-gray-600 outline-none cursor-pointer"
          >
            <option value="">All statuses</option>
            <option value="PAID">PAID</option>
            <option value="PENDING">PENDING</option>
            <option value="FAILED">FAILED</option>
            <option value="REFUNDED">REFUNDED</option>
          </select>
        )}
        {showMethod && (
          <select
            value={filterMethod}
            onChange={(e) => onMethodChange?.(e.target.value)}
            className="text-xs px-2.5 py-1.5 rounded-md border border-gray-200 bg-white text-gray-600 outline-none cursor-pointer"
          >
            <option value="">All methods</option>
            <option value="upi">UPI</option>
            <option value="card">Card</option>
            <option value="netbanking">Netbanking</option>
            <option value="wallet">Wallet</option>
            <option value="cash">Cash</option>
          </select>
        )}
        {extraFilters}
        {showPageSize && (
          <select
            value={take}
            onChange={(e) => onTakeChange(Number(e.target.value))}
            className="text-xs px-2.5 py-1.5 rounded-md border border-gray-200 bg-white text-gray-600 outline-none cursor-pointer ml-auto"
          >
            <option value={20}>20 / page</option>
            <option value={50}>50 / page</option>
            <option value={100}>100 / page</option>
          </select>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        {QUICK_DATE_RANGES.map(({ key, label, color }) => {
          const active = quickRange === key;
          const textColor = key === "last_fy" ? "#6B5B00" : color;
          return (
            <button
              key={key}
              type="button"
              onClick={() => onQuickRange(key)}
              className="text-xs px-3 py-1.5 rounded-md border transition cursor-pointer font-medium"
              style={{
                backgroundColor: active ? hexToRgba(color, 0.16) : hexToRgba(color, 0.06),
                borderColor: active ? color : hexToRgba(color, 0.4),
                color: textColor,
                boxShadow: active ? `0 0 0 1px ${hexToRgba(color, 0.25)}` : undefined,
              }}
            >
              {label}
            </button>
          );
        })}
      </div>
    </>
  );
}

export function OrdersExportButton({
  onClick,
  busy,
}: {
  onClick: () => void;
  busy?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={busy}
      className="flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-lg border transition cursor-pointer shadow-sm disabled:opacity-50"
      style={{
        borderColor: hexToRgba(BRAND.blue, 0.35),
        backgroundColor: hexToRgba(BRAND.blue, 0.06),
        color: BRAND.blue,
      }}
    >
      {busy ? "Preparing…" : "Export"}
    </button>
  );
}
